import React, { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import i18n from './config';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from './config';
import { ANTD_LOCALES, syncDayjsLocale } from './localeSynchronization';

interface I18nProviderProps {
  children: React.ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  // Initialize with saved language from localStorage
  const getSavedLanguage = (): SupportedLanguage => {
    const saved = localStorage.getItem('i18nextLng');
    if (saved && (saved === 'zh-CN' || saved === 'zh-HK' || saved === 'en')) {
      return saved as SupportedLanguage;
    }
    return 'zh-CN';
  };

  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(getSavedLanguage());
  const [isI18nReady, setIsI18nReady] = useState(false);

  useEffect(() => {
    // Handle language changes
    const handleLanguageChange = (lng: string) => {
      const language = lng as SupportedLanguage;
      if (SUPPORTED_LANGUAGES[language]) {
        setCurrentLanguage(language);
        syncDayjsLocale(language);
        // Ensure it's saved to localStorage
        localStorage.setItem('i18nextLng', language);
        // Update document language attribute
        document.documentElement.lang = language;
      }
    };

    // Set initial language from localStorage or i18n
    const initialLang = getSavedLanguage();
    if (i18n.isInitialized && i18n.language !== initialLang) {
      i18n.changeLanguage(initialLang).then(() => {
        handleLanguageChange(initialLang);
      });
    } else {
      handleLanguageChange(i18n.language);
    }

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    // Mark i18n as ready when initialized
    if (i18n.isInitialized) {
      setIsI18nReady(true);
    } else {
      i18n.on('initialized', () => {
        setIsI18nReady(true);
      });
    }

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Show loading state while i18n is initializing
  if (!isI18nReady) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '16px'
      }}>
        <Spin size="large" />
        <span style={{ marginLeft: 16 }}>加载中...</span>
      </div>
    );
  }

  // Get the appropriate Ant Design locale
  const antdLocale = ANTD_LOCALES[currentLanguage] || zhCN;

  return (
    <I18nextProvider i18n={i18n}>
      <ConfigProvider
        locale={antdLocale}
        theme={{
          token: {
            colorPrimary: '#1890ff',
            borderRadius: 6,
            fontSize: 14,
          },
          components: {
            Button: {
              paddingInlineSM: 12,
              paddingInline: 16,
              paddingInlineLG: 24,
            },
            Card: {
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            },
            Layout: {
              headerBg: '#001529',
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </I18nextProvider>
  );
};

export default I18nProvider;
